import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Select, Table, Tooltip, Modal, Switch, Form, Tag, Flex, notification, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    PrinterOutlined,
    EditOutlined,
    ExportOutlined,
    WarningOutlined,
    QuestionCircleOutlined,
    StopOutlined,
    DollarCircleFilled,
} from '@ant-design/icons';

import { usePDF } from '@react-pdf/renderer';
import printJS from 'print-js';

import Processing from '@controls/processing';
import Toolbar from '@controls/toolbar/toolbar';
import RemainingsPdf from '@print-forms/remainings/remainings-pdf';

import Filter from '@controls/filter/filter';
import FormHeader from '@controls/form-header/form-header';
import Comment from '@controls/comment/comment';
import { Tags, TransitWarehouseTag } from '@controls/tags';

import { delayAction, getDateTimeLocal, getDate } from '@extensions/utils';
import { exception, securityRestriction, warning } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { serverFetch } from '@flc/server';

import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ITruck } from '@entities/truck';
import { IUser } from '@entities/user';
import { IRemainingFilter } from '@entities/remaining-filter';
import { IRemaining } from '@entities/remaining';
import { IRemainingBoxGroup } from '@entities/remaining-box-group';
import { ICity } from '@entities/city';

import { Permission, hasPermission } from '@enums/permission';
import { UserType } from '@enums/user-type';

import { HoldIcon, UsdCircleIcon, UsdIcon } from '@core/icons';

dayjs.extend(utc);

interface IOnHoldParams {
    onHoldReason?: string;
}

const filterContext: string = 'Remainings';

const Remainings = () => {
    const initFilter: IRemainingFilter = { cityIds: [], toShippingOnly: true, shippedOnly: false, extraConsignmentsOnly: false };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const now = dayjs();

    const { journalId } = useParams();

    const [onHoldForm] = Form.useForm();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IRemainingFilter>((s) => s.filters[filterContext]);

    const d = useAppDispatch();

    const { TextArea } = Input;

    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [headers, setHeaders] = useState<Array<IRemaining>>([]);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [refreshFiltersRequired, setRefreshFiltersRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    const [printData, setPrintData] = useState<Array<IRemaining>>();
    const [startPrint, setStartPrint] = useState<boolean>(false);

    const [selectedCityRowKeys, setSelectedCityRowKeys] = useState<React.Key[]>([]);

    const [selectedBoxGroupRowKeys, setSelectedBoxGroupRowKeys] = useState<React.Key[]>([]);
    const [currentBoxGroup, setCurrentBoxGroup] = useState<IRemainingBoxGroup>();
    const [selectedMarkings, setSelectedMarkings] = useState<Array<string>>([]);
    const [selectedTruckIds, setSelectedTruckIds] = useState<Array<string | undefined>>([]);

    const [onHoldOpen, setOnHoldOpen] = useState<boolean>(false);
    const [onHoldProcessing, setOnHoldProcessing] = useState<boolean>(false);

    const [contractors, setContractors] = useState<Array<IUser>>([]);

    const [pdfInstance, updatePdf] = usePDF();

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch('users', { method: 'GET', queryParams: { types: UserType.Supplier, isActiveOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения поставщиков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setContractors(result[0][0] || []);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        let cleanup = false;
        if (!refreshFiltersRequired) return;

        const fetchData = async () => {
            const promises = [
                await serverFetch('remainings/cities', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('remainings/trucks', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0] || []);
                setTrucks(result[0][1] || []);

                setRefreshFiltersRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshFiltersRequired]);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedCityRowKeys([]);
        setSelectedBoxGroupRowKeys([]);
        setSelectedMarkings([]);
        setSelectedTruckIds([]);

        setHeaders([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('remainings/groups', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения остатков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setHeaders(result[0][0]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const onGenerateShipmentJournal = () => {
        setLoading(true);

        serverFetch(`shipmentjournals/generate/${journalId}`, {
            method: 'POST',
            bodyData: {
                cityKeys: selectedCityRowKeys,
                boxGroupIds: selectedBoxGroupRowKeys,
                markingCodes: selectedMarkings,
                truckIds: selectedTruckIds,
            },
        })
            .then((data) => {
                setLoading(false);
                navigate(`/flc/warehouse/shipmentjournals/${data}`);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка генерации журнала отгрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSaveComment = (boxGroupId: string, comment: string | undefined) => {
        serverFetch(`warehouse/boxgroup/${boxGroupId}/comment`, { method: 'POST', bodyData: comment })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка сохранения комментария', ex, () => d(userLoaded(undefined)));
            });
    };

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (printData) {
            updatePdf(<RemainingsPdf remainings={printData} />);
            setStartPrint(true);
        }
    }, [printData]);

    useEffect(() => {
        if (startPrint && !pdfInstance.loading && pdfInstance.blob) {
            setStartPrint(false);
            setPrintData(undefined);

            const blobURL = URL.createObjectURL(pdfInstance.blob);
            printJS(blobURL);
        }
    }, [startPrint, pdfInstance]);

    const onSetOnHold = (entity: IOnHoldParams) => {
        setOnHoldProcessing(true);

        serverFetch(`remainings/onhold`, {
            method: 'POST',
            bodyData: {
                ids: selectedBoxGroupRowKeys,
                onHoldReason: entity.onHoldReason,
            },
        })
            .then(() => {
                setOnHoldProcessing(false);

                onHoldForm.resetFields();
                setOnHoldOpen(false);

                setRefreshRequired(true);
            })
            .catch((ex) => {
                setOnHoldProcessing(false);
                exception(api, 'Ошибка блокировки грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: `${
                            hasPermission(userSession.permissions, Permission.ManageWarehouse) ? 'Изменить партию' : 'Информация о партии'
                        }`,
                        key: 'edit-consignment',
                        disabled: !currentBoxGroup,
                        icon: <EditOutlined />,
                        onClick: () => navigate(`/flc/warehouse/remainings/${currentBoxGroup?.boxGroupId}`),
                    },
                    {
                        label: 'Заблокировать / Разблокировать',
                        key: 'onHold',
                        disabled: selectedBoxGroupRowKeys.length <= 0 || loading,
                        icon: <HoldIcon />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            if (filter?.shippedOnly) {
                                warning(api, 'Груз уже отправлен');
                                return;
                            }

                            setOnHoldOpen(true);
                        },
                    },
                    {
                        label: 'Отгрузка',
                        type: 'primary',
                        key: 'shipment',
                        disabled: (selectedCityRowKeys.length < 1 && selectedBoxGroupRowKeys.length < 1) || loading,
                        icon: <ExportOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            if (filter?.shippedOnly) {
                                warning(api, 'Груз уже отправлен');
                                return;
                            }

                            onGenerateShipmentJournal();
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Печать',
                        key: 'printBarcode',
                        disabled: loading || startPrint,
                        icon: <PrinterOutlined />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageWarehouse) &&
                                !hasPermission(userSession.permissions, Permission.PrintDocuments)
                            ) {
                                securityRestriction(api, [Permission.ManageWarehouse, Permission.PrintDocuments]);
                                return;
                            }

                            setPrintData(headers);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Select
                            key='truck'
                            placeholder='Машина'
                            popupMatchSelectWidth={false}
                            value={filter?.truckIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, truckIds: value }, filterContext))}
                            showSearch={{
                                optionFilterProp: 'children',
                                filterOption: (input, option) => (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                            }}
                            options={trucks.map((t) => {
                                return { value: t.id, label: `${t.number} - ${getDate(t.departureOn)}` };
                            })}
                            disabled={filter?.extraConsignmentsOnly}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            showSearch={{
                                optionFilterProp: 'children',
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            maxTagCount='responsive'
                            key='contractor'
                            placeholder='Поставщик'
                            value={filter?.contractorIds}
                            size='middle'
                            mode='multiple'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, contractorIds: value }, filterContext))}
                            showSearch={{
                                optionFilterProp: 'children',
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={contractors.map((c) => {
                                return { value: c.id, label: c.login };
                            })}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Form.Item key='toShippingOnly' label='На отгрузку' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.toShippingOnly}
                                onChange={(value) => {
                                    d(setFilter({ ...filter, toShippingOnly: value }, filterContext));
                                }}
                                disabled={filter?.shippedOnly}
                            />
                        </Form.Item>,
                        <Form.Item key='extraConsignmentsOnly' label='Только догруз' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.extraConsignmentsOnly}
                                onChange={(value) => {
                                    d(setFilter({ ...filter, extraConsignmentsOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                        <Form.Item
                            key='shippedOnly'
                            label='Только отправленные'
                            style={{ margin: 0 }}
                            hidden={!hasPermission(userSession.permissions, Permission.ForceTransferBoxes)}
                        >
                            <Switch
                                checked={filter?.shippedOnly}
                                onChange={(value) => {
                                    setRefreshFiltersRequired(true);
                                    d(setFilter({ ...filter, shippedOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectBoxGroupChange = (selectedRowKeys: React.Key[]) => {
        setSelectedCityRowKeys([]);

        setSelectedBoxGroupRowKeys(selectedRowKeys);
        setSelectedMarkings([]);
        setSelectedTruckIds([]);

        const markings: Array<string> = [];
        const truckIds: Array<string | undefined> = [];

        if (selectedRowKeys.length === 1) {
            if (headers) {
                headers.map((h) => {
                    h.remainingBoxGroups.map((b) => {
                        if (b.boxGroupId === selectedRowKeys[0]) {
                            setCurrentBoxGroup(b);
                            markings.push(b.markingCode);
                            truckIds.push(b.truckId);
                        }
                    });
                });
            }
        } else {
            setCurrentBoxGroup(undefined);

            if (headers) {
                headers.map((h) => {
                    h.remainingBoxGroups.map((b) => {
                        const boxGroupId = selectedRowKeys.find((k) => k === b.boxGroupId);
                        if (boxGroupId) {
                            markings.push(b.markingCode);
                            truckIds.push(b.truckId);
                        }
                    });
                });
            }
        }

        setSelectedMarkings(markings);
        setSelectedTruckIds(truckIds);
    };

    const expandedRemainings = (record: IRemaining) => {
        const columns: ColumnsType<IRemainingBoxGroup> = [
            {
                title: 'ID',
                align: 'center',
                width: 80,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        color: record.consigneeIsLocked || record.hasBalanceDebt ? 'var(--main-red)' : '',
                        fontWeight: record.consigneeIsLocked || record.hasBalanceDebt ? 600 : '',
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex vertical justify='center' gap='small'>
                            <div>{record.consigneeCode}</div>

                            <Flex gap='small' justify='center'>
                                {record.consigneeIsLocked && (
                                    <Tooltip
                                        placement='top'
                                        title={`Грузополучатель заблокирован. ${record.consigneeLockReason}`}
                                        color='var(--main-red)'
                                    >
                                        <StopOutlined style={{ color: 'var(--main-red)', fontSize: 18 }} />
                                    </Tooltip>
                                )}

                                {record.hasBalanceDebt && (
                                    <Tooltip placement='top' title='Имеется задолжность по платежам' color='var(--main-red)'>
                                        <DollarCircleFilled style={{ color: 'var(--main-red)', fontSize: 18 }} />
                                    </Tooltip>
                                )}
                            </Flex>

                            <TransitWarehouseTag warehouseCode={record.warehouseCode} />
                        </Flex>
                    );
                },
            },
            {
                title: ' ',
                width: 32,
                align: 'center',
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record: IRemainingBoxGroup) => {
                    let diffHours: number = 0;
                    if (record.acceptedOn) {
                        diffHours = now.diff(record.acceptedOn, 'hour');
                    }

                    return (
                        <>
                            {record.onHold && (
                                <Tooltip placement='top' title={`Запрет отгрузки. ${record.onHoldReason}`} color='darkorchid'>
                                    <div>
                                        <HoldIcon style={{ color: 'darkorchid', fontSize: 18 }} />
                                    </div>
                                </Tooltip>
                            )}

                            {diffHours >= 24 ? (
                                <Tooltip placement='top' title='Хранение более 24 часов на складе' color='red'>
                                    <div>
                                        <WarningOutlined style={{ color: 'red', fontSize: 18 }} />
                                    </div>
                                </Tooltip>
                            ) : diffHours >= 10 ? (
                                <Tooltip placement='top' title='Хранение более 10 часов на складе' color='orange'>
                                    <div>
                                        <WarningOutlined style={{ color: 'orange', fontSize: 18 }} />
                                    </div>
                                </Tooltip>
                            ) : (
                                <></>
                            )}
                        </>
                    );
                },
            },
            {
                title: ' ',
                width: 32,
                align: 'center',
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_: any, record: IRemainingBoxGroup) => {
                    return (
                        <Comment
                            editMode={true}
                            comment={record.comment}
                            onSave={(e) => {
                                if (record.boxGroupId) onSaveComment(record.boxGroupId, e);
                            }}
                        />
                    );
                },
            },
            {
                title: 'Желаемая отгрузка',
                dataIndex: 'shippingPriorityOn',
                width: 180,
                align: 'center',
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record: IRemainingBoxGroup) => {
                    if (!record.shippingPriorityOn) return;
                    const shippingPriorityOn = dayjs(record.shippingPriorityOn).utc(true).set('hour', 0).set('minute', 0).set('second', 0);

                    const now = dayjs().utc(true).set('hour', 0).set('minute', 0).set('second', 0);
                    let color = 'green';
                    if (shippingPriorityOn == now) {
                        color = 'orange';
                    } else if (shippingPriorityOn < now) {
                        color = 'red';
                    }

                    return (
                        record.awbNumber && (
                            <Tag color={color} variant='outlined' style={{ marginLeft: 10, fontWeight: 600, color: '#000000' }}>
                                {getDate(shippingPriorityOn)}
                            </Tag>
                        )
                    );
                },
            },
            {
                title: 'Маркировка',
                dataIndex: 'markingCode',
                width: 200,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
            },
            {
                title: 'Страна',
                width: 190,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex vertical>
                            {record.countryName}

                            <Tag
                                variant='solid'
                                color={record.itemColor}
                                style={{
                                    marginBottom: 2,
                                    paddingBottom: 2,
                                    width: 'fit-content',
                                    fontSize: 15,
                                    fontWeight: 600,
                                }}
                            >
                                {record.itemName}
                            </Tag>
                        </Flex>
                    );
                },
            },

            {
                title: 'AWB',
                width: 130,
                align: 'center',
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        record.awbNumber && (
                            <Tag color='cyan' variant='outlined' style={{ marginLeft: 10, color: '#000000' }}>
                                {record.awbNumber}
                            </Tag>
                        )
                    );
                },
            },
            {
                title: 'Отгрузка',
                align: 'center',
                width: 100,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record: IRemainingBoxGroup) => {
                    return getDate(record.loadingOn);
                },
            },
            {
                title: 'Машина',
                width: 150,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return record.truckNumber || '- догруз -';
                },
            },
            {
                title: 'Приемка',
                align: 'center',
                width: 140,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '',
                    },
                }),
                render: (_: any, record: IRemainingBoxGroup) => {
                    return getDateTimeLocal(record.acceptedOn);
                },
            },
            {
                title: 'Кол-во / шт',
                dataIndex: 'qty',
                align: 'center',
                width: 120,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record: IRemainingBoxGroup) => {
                    if (record.boxGroupQty == record.qty) return record.qty;

                    return (
                        <Tooltip title='Расхождение с расчетными документами' color='var(--main-red)'>
                            <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>{record.qty}</span>
                            <span> / {record.boxGroupQty}</span>
                        </Tooltip>
                    );
                },
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: (record: IRemainingBoxGroup) => ({
                    style: {
                        backgroundColor: record.consigneeIsLocked || record.hasBalanceDebt ? '#fbe7eb' : '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record: IRemainingBoxGroup) => {
                    return record.calcVolume?.toFixed(2);
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='boxGroupId'
                size='small'
                columns={columns}
                dataSource={record.remainingBoxGroups || []}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedBoxGroupRowKeys,
                    onChange: onSelectBoxGroupChange,
                    columnWidth: 32,
                }}
            />
        );
    };

    const onSelectCityChange = (selectedRowKeys: React.Key[]) => {
        setSelectedCityRowKeys(selectedRowKeys);
        setSelectedBoxGroupRowKeys([]);
        setSelectedMarkings([]);
        setSelectedTruckIds([]);
    };

    const renderTable = () => {
        const columns: ColumnsType<IRemaining> = [
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 800,
                onCell: () => ({
                    style: {
                        fontWeight: 700,
                    },
                }),
                render: (_, record: IRemaining) => {
                    let diffHours: number = 0;
                    if (record.acceptedOn) {
                        diffHours = now.diff(record.acceptedOn, 'hour');
                    }

                    return (
                        <Flex gap='small'>
                            <Flex align='start'>
                                <span>{record.cityName}</span>

                                {record.warehouseCode && (
                                    <Tag variant='solid' color='#3e4657' style={{ marginLeft: 10 }}>
                                        {record.warehouseCode}
                                    </Tag>
                                )}

                                {diffHours >= 24 ? (
                                    <WarningOutlined style={{ color: 'red', fontSize: 18, marginLeft: 5 }} />
                                ) : diffHours >= 10 ? (
                                    <WarningOutlined style={{ color: 'orange', fontSize: 18, marginLeft: 5 }} />
                                ) : (
                                    <></>
                                )}
                            </Flex>
                            <Flex wrap gap='small'>
                                {record.billCityName != record.cityName && (
                                    <Tag color='#FFA500' variant='solid' style={{ marginLeft: 10, color: '#000000' }}>
                                        #{record.billCityName} - самовывоз
                                    </Tag>
                                )}

                                {record.tag && <Tags label={record.tag} />}
                            </Flex>

                            {record.hasDiscrepancy && (
                                <Tag variant='outlined' color='var(--main-red)'>
                                    Есть расхождения с расчетными документами
                                </Tag>
                            )}
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                dataIndex: 'totalQty',
                key: 'totalQty',
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
                render: (_: any, record: IRemaining) => {
                    return record.totalVolume.toFixed(2);
                },
            },
            { title: ' ' },
        ];

        return (
            <Table
                rowKey='cityKey'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                expandable={{
                    expandedRowRender: (record) => expandedRemainings(record),
                    columnWidth: 32,
                }}
                dataSource={headers}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedCityRowKeys,
                    onChange: onSelectCityChange,
                    columnWidth: 32,
                }}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <>
            <div ref={containerRef} style={{ height: `calc(100vh - 192px - ${filterHeight}px)` }}>
                {journalId && <FormHeader title={`Добавить в журнал`} />}
                {renderToolbar()}
                {renderFilter()}
                {headers && renderTable()}

                {startPrint && <Processing title='Печать...' />}

                {contextHolder}
            </div>

            <Modal
                title={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'start' }}>
                        <QuestionCircleOutlined style={{ color: '#faad14', marginInlineEnd: 12, fontSize: 22 }} />
                        {`Заблокировать / разблокировать выделенные позиции?`}
                    </div>
                }
                open={onHoldOpen}
                okText='Сохранить'
                onOk={() => onHoldForm.submit()}
                onCancel={() => {
                    onHoldForm.resetFields();
                    setOnHoldOpen(false);
                }}
                closable={false}
                cancelButtonProps={{ style: { display: onHoldProcessing ? 'none' : '' } }}
                confirmLoading={onHoldProcessing}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    onFinish={onSetOnHold}
                    form={onHoldForm}
                    style={{ marginTop: 10 }}
                >
                    <Form.Item
                        required={true}
                        label='Причина'
                        name='onHoldReason'
                        rules={[{ required: true, message: 'Укажите причину блокировки' }]}
                    >
                        <TextArea autoFocus rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default Remainings;
